import { AnimationWorkflow } from "@/lib/hooks/useAnimationWorkflow";

// Example: Custom bounce effect workflow
export const bounceWorkflow: AnimationWorkflow = {
  id: "bounce-effect",
  name: "Bounce Animation",
  steps: [
    {
      id: "bounce-up",
      name: "Bounce Up",
      duration: 300,
      easing: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      transform: {
        translateY: -30,
        scale: 1.1,
      },
    },
    {
      id: "bounce-down",
      name: "Bounce Down",
      duration: 300,
      easing: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      transform: {
        translateY: 0,
        scale: 1,
      },
    },
  ],
};

// Example: Complex multi-step workflow with callbacks
export const complexCardRevealWorkflow = (
  onFlipStart?: () => void,
  onCardRevealed?: () => void,
  onPositioned?: () => void,
  onComplete?: () => void,
): AnimationWorkflow => ({
  id: "complex-card-reveal",
  name: "Complex Card Reveal with Multiple Effects",
  steps: [
    {
      id: "initial-glow",
      name: "Initial Glow Effect",
      duration: 500,
      easing: "ease-in-out",
      transform: {
        scale: 1.05,
      },
      onStart: onFlipStart,
      onComplete: () => console.log("Glow effect complete"),
    },
    {
      id: "flip-start",
      name: "Begin Flip Animation",
      duration: 800,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotate: "90deg",
        scale: 1.05,
      },
      opacity: 0.3,
    },
    {
      id: "reveal-card",
      name: "Reveal New Card",
      duration: 800,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      transform: {
        rotate: "180deg",
        scale: 1.05,
      },
      opacity: 1,
      onStart: onCardRevealed,
      onComplete: () => console.log("Card revealed"),
    },
    {
      id: "normalize-scale",
      name: "Normalize Scale",
      duration: 400,
      delay: 200,
      easing: "ease-out",
      transform: {
        rotate: "180deg",
        scale: 1,
      },
    },
    {
      id: "move-to-corner",
      name: "Move to Corner Position",
      duration: 1200,
      delay: 300,
      easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      transform: {
        translateX: "-35vw",
        translateY: "-25vh",
        scale: 0.45,
        rotate: "180deg",
      },
      onStart: () => console.log("Starting move to corner"),
      onComplete: onPositioned,
    },
    {
      id: "final-fade",
      name: "Final Fade Effect",
      duration: 300,
      opacity: 0.8,
      onComplete: () => {
        console.log("Animation sequence complete");
        if (onComplete) onComplete();
      },
    },
  ],
  onWorkflowComplete: () => {
    console.log("Complex card reveal workflow finished");
  },
});

// Example: Simple utility workflows
export const fadeInUpWorkflow = (
  duration: number = 600,
): AnimationWorkflow => ({
  id: "fade-in-up",
  name: "Fade In Up",
  steps: [
    {
      id: "start-below",
      name: "Start Below and Hidden",
      duration: 0,
      transform: {
        translateY: 30,
      },
      opacity: 0,
    },
    {
      id: "fade-in-up-step",
      name: "Fade and Slide Up",
      duration,
      easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      transform: {
        translateY: 0,
      },
      opacity: 1,
    },
  ],
});

export const scaleInWorkflow = (
  targetScale: number = 1,
  duration: number = 400,
): AnimationWorkflow => ({
  id: "scale-in",
  name: "Scale In Animation",
  steps: [
    {
      id: "start-small",
      name: "Start Small and Hidden",
      duration: 0,
      transform: {
        scale: 0.3,
      },
      opacity: 0,
    },
    {
      id: "scale-in-step",
      name: "Scale to Target",
      duration,
      easing: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      transform: {
        scale: targetScale,
      },
      opacity: 1,
    },
  ],
});

// Example: Workflow with custom coordinates and configurable options
export const createSlideWorkflow = (options: {
  fromX?: number;
  fromY?: number;
  toX?: number;
  toY?: number;
  duration?: number;
  easing?: string;
  onComplete?: () => void;
}): AnimationWorkflow => {
  const {
    fromX = 0,
    fromY = 50,
    toX = 0,
    toY = 0,
    duration = 500,
    easing = "ease-out",
    onComplete,
  } = options;

  return {
    id: "custom-slide",
    name: "Custom Slide Animation",
    steps: [
      {
        id: "start-position",
        name: "Set Start Position",
        duration: 0,
        transform: {
          translateX: fromX,
          translateY: fromY,
        },
        opacity: 1,
      },
      {
        id: "slide-step",
        name: "Slide to Position",
        duration,
        easing,
        transform: {
          translateX: toX,
          translateY: toY,
        },
        opacity: 1,
        onComplete,
      },
    ],
  };
};
